/**
 * Edit a requisition's fields — `PATCH /recruiting/requisitions/{id}`, WP-W1.
 *
 * A partial update: the body carries **only the fields the operator changed**, and
 * `RecruitingRequisitionUpdateRequest` requires at least one. The submit is disabled
 * until something differs from the loaded requisition — a true statement about the
 * operator ("nothing to save"), which is also the shape that keeps an empty PATCH,
 * refused `400 minProperties`, from ever being sent.
 *
 * `status` is **not** here — it moves through `RequisitionStatusControls`, against the
 * state machine, so a field edit and a lifecycle move never share a form. The
 * nullable fields (`location`, `employment_type`) send `null` to clear, which is a
 * different request from omitting them; `title` cannot be cleared, so a blanked title
 * is simply not part of the change set.
 *
 * There is no `If-Match` on this endpoint — `openapi.yaml` gives requisition PATCH no
 * precondition — so a save re-reads afterward to show the requisition as it now is,
 * rather than carrying an ETag it was never handed.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  EmploymentType,
  RecruitingRequisition,
  RecruitingRequisitionUpdateRequest,
} from '../../api/recruiting';
import { EMPLOYMENT_TYPES } from '../../api/recruiting';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

const HEADCOUNT_MIN = 1;
const TITLE_MAX = 500;

export interface RequisitionEditFormProps {
  readonly requisition: RecruitingRequisition;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly saved: boolean;
  readonly onSubmit: (body: RecruitingRequisitionUpdateRequest) => void;
}

export function RequisitionEditForm({
  requisition,
  pending,
  error,
  saved,
  onSubmit,
}: RequisitionEditFormProps): ReactNode {
  const { t } = useI18n();
  const [title, setTitle] = useState(requisition.title);
  const [headcount, setHeadcount] = useState(String(requisition.headcount));
  const [location, setLocation] = useState(requisition.location ?? '');
  const [employmentType, setEmploymentType] = useState<'' | EmploymentType>(
    requisition.employment_type ?? '',
  );

  const parsedHeadcount = Number.parseInt(headcount, 10);
  const headcountValid = Number.isFinite(parsedHeadcount) && parsedHeadcount >= HEADCOUNT_MIN;

  // The change set, computed once so the submit's enabled state and the body it sends
  // cannot disagree. A nullable field blanked becomes `null` (clear); `title` blanked
  // is not a change, because the field is non-nullable and the empty string is invalid.
  // Built mutably then handed over as the readonly request shape.
  const draft: {
    title?: string;
    headcount?: number;
    location?: string | null;
    employment_type?: EmploymentType | null;
  } = {};
  const trimmedTitle = title.trim();
  if (trimmedTitle !== '' && trimmedTitle !== requisition.title) draft.title = trimmedTitle;
  if (headcountValid && parsedHeadcount !== requisition.headcount)
    draft.headcount = parsedHeadcount;
  const trimmedLocation = location.trim();
  const currentLocation = requisition.location ?? '';
  if (trimmedLocation !== currentLocation) {
    draft.location = trimmedLocation === '' ? null : trimmedLocation;
  }
  const currentType = requisition.employment_type ?? '';
  if (employmentType !== currentType) {
    draft.employment_type = employmentType === '' ? null : employmentType;
  }

  const change: RecruitingRequisitionUpdateRequest = draft;
  const hasChange = Object.keys(draft).length > 0;

  return (
    <section className="wp-section" aria-labelledby="w1-edit-heading" data-testid="w1-edit">
      <h3 className="wp-section__title" id="w1-edit-heading">
        {t('recruiting.jobs.edit.title')}
      </h3>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (!hasChange || !headcountValid || pending) return;
          onSubmit(change);
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-edit-title">
            {t('recruiting.jobs.edit.titleLabel')}
          </label>
          <input
            className="wp-field__input"
            id="w1-edit-title"
            data-testid="w1-edit-title"
            type="text"
            value={title}
            maxLength={TITLE_MAX}
            autoComplete="off"
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-edit-headcount">
            {t('recruiting.jobs.edit.headcount')}
          </label>
          <input
            className="wp-field__input"
            id="w1-edit-headcount"
            data-testid="w1-edit-headcount"
            type="number"
            min={HEADCOUNT_MIN}
            value={headcount}
            onChange={(event) => {
              setHeadcount(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-edit-location">
            {t('recruiting.jobs.edit.location')}
          </label>
          <input
            className="wp-field__input"
            id="w1-edit-location"
            data-testid="w1-edit-location"
            type="text"
            value={location}
            autoComplete="off"
            onChange={(event) => {
              setLocation(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-edit-type">
            {t('recruiting.jobs.edit.employmentType')}
          </label>
          <select
            className="wp-field__input"
            id="w1-edit-type"
            data-testid="w1-edit-type"
            value={employmentType}
            onChange={(event) => {
              setEmploymentType(event.target.value as '' | EmploymentType);
            }}
          >
            <option value="">{t('recruiting.jobs.employmentType.none')}</option>
            {EMPLOYMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`recruiting.jobs.employmentType.${type}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w1-edit-submit"
            disabled={!hasChange || !headcountValid || pending}
          >
            {pending ? t('recruiting.jobs.edit.saving') : t('recruiting.jobs.edit.save')}
          </button>
          {hasChange ? null : (
            <p className="wp-small wp-muted" data-testid="w1-edit-nochange">
              {t('recruiting.jobs.edit.noChange')}
            </p>
          )}
        </div>
      </form>

      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('recruiting.jobs.subjectWord')}
          testId="w1-edit-error"
        />
      )}
      {saved ? (
        <StatusNote severity="low" title={t('recruiting.jobs.edit.ok')} testId="w1-edit-ok" />
      ) : null}
    </section>
  );
}
